/**
 * Hotel Filters Component
 * Handles hotel search filters functionality
 */

import { HotelResult, HotelFilters } from '../types/data-models';

export class HotelFiltersComponent {
  private container: HTMLElement;
  private priceMinInput!: HTMLInputElement;
  private priceMaxInput!: HTMLInputElement;
  private ratingCheckboxes!: NodeListOf<HTMLInputElement>;
  private amenityCheckboxes!: NodeListOf<HTMLInputElement>;
  private clearButton!: HTMLButtonElement;

  public onFilterChange: ((filters: HotelFilters) => void) | null = null;
  public onClearFilters: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.initializeElements();
    this.bindEvents();
  }

  private initializeElements(): void {
    this.priceMinInput = this.container.querySelector('#price-min') as HTMLInputElement;
    this.priceMaxInput = this.container.querySelector('#price-max') as HTMLInputElement;
    this.ratingCheckboxes = this.container.querySelectorAll('input[name="rating"]');
    this.amenityCheckboxes = this.container.querySelectorAll('input[name="amenities"]');
    this.clearButton = this.container.querySelector('.search-filters__clear') as HTMLButtonElement;
  }

  private bindEvents(): void {
    // Price range inputs
    if (this.priceMinInput) {
      this.priceMinInput.addEventListener('input', () => this.handleFilterChange());
      this.priceMinInput.addEventListener('blur', () => this.validatePriceRange());
    }

    if (this.priceMaxInput) {
      this.priceMaxInput.addEventListener('input', () => this.handleFilterChange());
      this.priceMaxInput.addEventListener('blur', () => this.validatePriceRange());
    }

    // Rating checkboxes
    this.ratingCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleFilterChange());
    });

    // Amenity checkboxes
    this.amenityCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleFilterChange());
    });

    // Clear filters button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearAllFilters());
    }
  }

  private validatePriceRange(): void {
    const minValue = parseInt(this.priceMinInput.value) || 0;
    const maxValue = parseInt(this.priceMaxInput.value) || 1000;

    if (minValue > maxValue) {
      if (this.priceMinInput === document.activeElement) {
        // User was editing min price, adjust max
        this.priceMaxInput.value = minValue.toString();
      } else {
        // User was editing max price, adjust min
        this.priceMinInput.value = maxValue.toString();
      }
      this.handleFilterChange();
    }
  }

  private handleFilterChange(): void {
    const filters = this.getCurrentFilters();
    if (this.onFilterChange) {
      this.onFilterChange(filters);
    }
  }

  private getCurrentFilters(): HotelFilters {
    const filters: HotelFilters = {};

    // Price range filter
    const minPrice = parseInt(this.priceMinInput.value) || 0;
    const maxPrice = parseInt(this.priceMaxInput.value) || 1000;
    
    if (minPrice > 0 || maxPrice < 1000) {
      filters.priceRange = {
        min: minPrice,
        max: maxPrice,
        currency: 'EUR'
      };
    }

    // Rating filter
    const selectedRatings: number[] = [];
    this.ratingCheckboxes.forEach(checkbox => {
      if (checkbox.checked) {
        selectedRatings.push(parseInt(checkbox.value));
      }
    });
    
    if (selectedRatings.length > 0) {
      filters.rating = selectedRatings;
    }

    // Amenities filter
    const selectedAmenities: string[] = [];
    this.amenityCheckboxes.forEach(checkbox => {
      if (checkbox.checked) {
        selectedAmenities.push(checkbox.value);
      }
    });
    
    if (selectedAmenities.length > 0) {
      filters.amenities = selectedAmenities;
    }

    return filters;
  }

  public updateAvailableFilters(hotels: HotelResult[]): void {
    this.updatePriceRange(hotels);
    this.updateAmenityFilters(hotels);
  }

  private updatePriceRange(hotels: HotelResult[]): void {
    if (hotels.length === 0) return;

    const prices = hotels.map(hotel => hotel.price.min);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // Update placeholder values
    if (this.priceMinInput && !this.priceMinInput.value) {
      this.priceMinInput.placeholder = minPrice.toString();
    }
    
    if (this.priceMaxInput && !this.priceMaxInput.value) {
      this.priceMaxInput.placeholder = maxPrice.toString();
    }

    // Update input constraints
    this.priceMinInput.min = minPrice.toString();
    this.priceMinInput.max = maxPrice.toString();
    this.priceMaxInput.min = minPrice.toString();
    this.priceMaxInput.max = maxPrice.toString();
  }

  private updateAmenityFilters(hotels: HotelResult[]): void {
    // Count amenity occurrences
    const amenityCounts: { [key: string]: number } = {};
    
    hotels.forEach(hotel => {
      hotel.amenities.forEach(amenity => {
        const key = amenity.toLowerCase();
        amenityCounts[key] = (amenityCounts[key] || 0) + 1;
      });
    });

    // Update amenity filter labels with counts
    this.amenityCheckboxes.forEach(checkbox => {
      const amenityValue = checkbox.value;
      const count = amenityCounts[amenityValue] || 0;
      const label = checkbox.parentElement?.querySelector('.filter-checkbox__label');
      
      if (label) {
        const originalText = label.textContent?.split(' (')[0] || '';
        if (count > 0) {
          label.textContent = `${originalText} (${count})`;
          checkbox.parentElement!.style.display = 'flex';
        } else {
          checkbox.parentElement!.style.display = 'none';
        }
      }
    });
  }

  public clearAllFilters(): void {
    // Clear price inputs
    if (this.priceMinInput) this.priceMinInput.value = '';
    if (this.priceMaxInput) this.priceMaxInput.value = '';

    // Uncheck all checkboxes
    this.ratingCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    this.amenityCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Trigger filter change
    if (this.onClearFilters) {
      this.onClearFilters();
    } else {
      this.handleFilterChange();
    }
  }

  public setFilters(filters: HotelFilters): void {
    // Set price range
    if (filters.priceRange) {
      if (this.priceMinInput) this.priceMinInput.value = filters.priceRange.min.toString();
      if (this.priceMaxInput) this.priceMaxInput.value = filters.priceRange.max.toString();
    }

    // Set rating checkboxes
    this.ratingCheckboxes.forEach(checkbox => {
      checkbox.checked = filters.rating?.includes(parseInt(checkbox.value)) || false;
    });

    // Set amenity checkboxes
    this.amenityCheckboxes.forEach(checkbox => {
      checkbox.checked = filters.amenities?.includes(checkbox.value) || false;
    });
  }

  public getActiveFiltersCount(): number {
    let count = 0;

    // Count price filter
    if (this.priceMinInput.value || this.priceMaxInput.value) {
      count++;
    }

    // Count rating filters
    const checkedRatings = Array.from(this.ratingCheckboxes).filter(cb => cb.checked);
    if (checkedRatings.length > 0) {
      count++;
    }

    // Count amenity filters
    const checkedAmenities = Array.from(this.amenityCheckboxes).filter(cb => cb.checked);
    if (checkedAmenities.length > 0) {
      count++;
    }

    return count;
  }

  public updateClearButtonState(): void {
    const activeFiltersCount = this.getActiveFiltersCount();
    
    if (this.clearButton) {
      this.clearButton.disabled = activeFiltersCount === 0;
      this.clearButton.textContent = activeFiltersCount > 0 
        ? `Effacer les filtres (${activeFiltersCount})` 
        : 'Effacer les filtres';
    }
  }
}